import { uploadApis } from "@payping/api";
import { InfoCircleFilled, UploadOutlined } from "@payping/icons";
import { ValidNationalId } from "@payping/utils/validation";
import { Col, Form, Input, Radio, Row, Space, Typography, Upload } from "@payping/web-components";
import { UploadChangeParam, UploadFile } from "@payping/web-components/components/Upload/types";
import React, { ReactElement } from "react";
import { useAppTranslation } from "@/utils/useAppTranslation";
import styles from "@/assets/scss/dashCustomer/form.module.scss";

interface BaseFormProps {
  phoneHelp?: string;
  emailHelp?: string;
  loading?: boolean;
  fileList: UploadFile[];
  setFileList: React.Dispatch<React.SetStateAction<UploadFile[]>>;
}

const BaseForm: React.FC<BaseFormProps> = (props: BaseFormProps) => {
  const { emailHelp, loading, phoneHelp, fileList, setFileList } = props;
  const { t } = useAppTranslation();

  const uploadButton = (
    <div>
      <UploadOutlined width={"14px"} className={"color--primary"} />
      <div className={"ant-upload-text"}>{t("common.uploadPhoto")}</div>
    </div>
  );

  return (
    <>
      <Form.Item name={"isBusiness"} label={t("customer.typeTitle")} className={styles.customerType}>
        <Radio.Group buttonStyle={"solid"}>
          <Radio.Button value={false}>{t("customer.type.natural")}</Radio.Button>
          <Radio.Button value={true}>{t("customer.type.legal")}</Radio.Button>
        </Radio.Group>
      </Form.Item>
      <Form.Item
        noStyle
        shouldUpdate={(prevValues, currentValues): boolean => prevValues.isBusiness !== currentValues.isBusiness}
      >
        {(form1): ReactElement => (
          <Row gutter={[16, 8]}>
            {form1.getFieldValue("isBusiness") === false ? (
              <>
                <Col xs={24} lg={12}>
                  <Form.Item label={t("firstName")} name={"firstName"}>
                    <Input skeleton={loading} />
                  </Form.Item>
                </Col>
                <Col xs={24} lg={12}>
                  <Form.Item label={t("lastName")} name={"lastName"}>
                    <Input skeleton={loading} />
                  </Form.Item>
                </Col>
              </>
            ) : null}
          </Row>
        )}
      </Form.Item>
      <Form.Item
        noStyle
        shouldUpdate={(prevValues, currentValues): boolean => prevValues.isBusiness !== currentValues.isBusiness}
      >
        {({ getFieldValue }) =>
          getFieldValue("isBusiness") ? (
            <Row gutter={[16, 8]}>
              <Col xs={24} lg={12}>
                <Form.Item label={t("businessName")} name={"businessName"}>
                  <Input skeleton={loading} />
                </Form.Item>
              </Col>
              <Col xs={24} lg={7}>
                <Form.Item label={t("shenaseMelli")} name={"bNationalCode"}>
                  <Input skeleton={loading} onlyNumbers canStartWithZero />
                </Form.Item>
              </Col>
              <Col xs={24} lg={7}>
                <Form.Item label={t("common.economyCode")} name={"economicCode"}>
                  <Input skeleton={loading} onlyNumbers canStartWithZero />
                </Form.Item>
              </Col>
              <Col xs={24} lg={7}>
                <Form.Item label={t("common.operation.sabtNumber")} name={"sabtNumber"}>
                  <Input skeleton={loading} onlyNumbers canStartWithZero />
                </Form.Item>
              </Col>
            </Row>
          ) : (
            <Row>
              <Col xs={24} lg={7}>
                <Form.Item
                  label={t("nationalId")}
                  name={"nationalId"}
                  rules={[
                    {
                      validator: (_rule, value): Promise<void> =>
                        value && !ValidNationalId(value) ? Promise.reject(t("error.nationalId")) : Promise.resolve(),
                    },
                  ]}
                >
                  <Input skeleton={loading} onlyNumbers canStartWithZero />
                </Form.Item>
              </Col>
            </Row>
          )
        }
      </Form.Item>
      <Row gutter={[16, 8]}>
        <Col span={24}>
          <Space className={"tw-mb-4"}>
            <InfoCircleFilled width={10} className={"color--cta"} />
            <Typography.Text>{t("error.onlyPhoneOrEmailRequired")}</Typography.Text>
          </Space>
        </Col>
        <Col xs={24} lg={7}>
          <Form.Item
            required
            label={t("product.form.phone.mobile")}
            name={"phone"}
            validateStatus={phoneHelp ? "error" : "success"}
            help={phoneHelp}
          >
            <Input skeleton={loading} onlyNumbers canStartWithZero />
          </Form.Item>
        </Col>
        <Col xs={24} lg={12}>
          <Form.Item
            label={t("email")}
            name={"email"}
            validateStatus={emailHelp ? "error" : "success"}
            help={emailHelp}
          >
            <Input skeleton={loading} />
          </Form.Item>
        </Col>
      </Row>

      <Space direction={"vertical"}>
        <Typography.Text>{t("customer.form.upload.placeholder")}</Typography.Text>
        <Form.Item name={"avatar"}>
          <Upload
            disabled={loading}
            listType={"picture-card"}
            requestFunction={uploadApis.customerPicWithFilePost}
            onChange={(info: UploadChangeParam): void => setFileList(info.fileList)}
            fileList={fileList}
            showUploadList={{ showPreviewIcon: true, showRemoveIcon: true }}
            allowedExtensions={["jpg", "jpeg", "png"]}
            showTooltip
          >
            {fileList.length ? null : uploadButton}
          </Upload>
        </Form.Item>
      </Space>
    </>
  );
};

export default BaseForm;
